import App from './App.vue';
import Vue from 'vue';
import VueRouter from 'vue-router';
import TheModalWindow from '@/components/TheModalWindow.vue';
import store from './store';

// Plugins
import DashboardPlugin from './material-dashboard';
import Toasted from 'vue-toasted';
import VueConfetti from 'vue-confetti';
import VueCarousel from 'vue-carousel';
import Chartist from 'chartist';
import Particles from '@tsparticles/vue2';
import { loadSlim } from '@tsparticles/slim';
import VuePrism from 'vue-prism';
import { AgGridVue } from 'ag-grid-vue'; // Vue Data Grid Component
import { clickOutside } from './directives/clickOutside';
import { isOnboardingCompletedLocally } from '@/utils/onboardingCompletion';
import VueShepherd from 'vue-shepherd';
import { i18n } from './i18n';

// Styles
import 'prismjs/themes/prism.css';
import 'ag-grid-community/styles/ag-grid.css'; // Mandatory CSS required by the Data Grid
import 'ag-grid-community/styles/ag-theme-quartz.css'; // Optional Theme applied to the Data Grid
import 'shepherd.js/dist/css/shepherd.css';
import './assets/scss/shepherd-custom.scss';
import 'material-symbols';
import 'material-icons/iconfont/material-icons.css';

// Material 3 components
import './material3-components';
import './assets/scss/material-web-components.scss';

// Lixsa canonical design tokens (brand colour, slate neutrals, semantics)
import './assets/scss/brand-tokens.scss';
import './assets/scss/lx-picker.scss';
// Tokens de la clase raiz .obv3 (espaciados, alturas de campo, Material Web).
// Global a proposito: todo el fichero cuelga de `.obv3`, asi que solo pinta
// donde esa clase esta puesta, pero ya no depende de que el chunk del
// onboarding o del login se haya cargado antes. Ver docs/UI-GUIDE.md 4.
import './assets/scss/onboarding-v3.scss';

// MDUI Components
import 'mdui/mdui.css';
import 'mdui';
import { setColorScheme } from 'mdui';
setColorScheme('#6551E8');
// setColorScheme derives a darker tone-40 primary, which made <mdui-button>s
// look noticeably darker than the vue-material / Material 3 buttons. Pin MDUI's
// primary to the EXACT brand colour (#6551E8 = rgb 101,81,232) so all three
// button systems render the same purple.
const LX_PRIMARY_RGB = '101,81,232';
document.documentElement.style.setProperty('--mdui-color-primary', LX_PRIMARY_RGB);
document.documentElement.style.setProperty('--mdui-color-primary-light', LX_PRIMARY_RGB);
import './assets/scss/mdui-components.scss';

// Lixsa button normalization — loaded last so it wins the cascade
import './assets/scss/lixsa-buttons.scss';

// Router setup
import routes from './routes/routes';
import { TIENDANUBE_SKILL_ID } from '@/components/Skills/MainApps/tiendanubeOAuth';

import './registerServiceWorker';

// Stripe script
const stripeScript = document.createElement('script');
stripeScript.src = 'https://js.stripe.com/v3/pricing-table.js';
stripeScript.async = true;
document.head.appendChild(stripeScript);

// Tolt script
const toltScript = document.createElement('script');
toltScript.src = 'https://cdn.tolt.io/tolt.js';
toltScript.async = true;
toltScript.setAttribute('data-tolt', process.env.VUE_APP_TOLT_ID);
document.head.appendChild(toltScript);

// Clarity script with error handling
const clarityScript = document.createElement('script');
clarityScript.type = 'text/javascript';
clarityScript.onerror = (error) => {
    console.error('Failed to load Clarity script:', error);
};
clarityScript.innerHTML = `
    try {
        (function(c,l,a,r,i,t,y){
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
        })(window, document, "clarity", "script", "oifgotxm83");
    } catch (error) {
        console.error('Error initializing Clarity:', error);
    }
`;
document.head.appendChild(clarityScript);

// Google Analytics script
const gtagScript = document.createElement('script');
gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-K0XLSE9R9H';
gtagScript.async = true;
document.head.appendChild(gtagScript);

const gtagConfigScript = document.createElement('script');
gtagConfigScript.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', 'G-K0XLSE9R9H', {
        debug_mode: ${process.env.VUE_APP_ENVIROMENT === 'local'}
    });
    window.GA_INITIALIZED = true;
`;
document.head.appendChild(gtagConfigScript);

// Meta pixel script
const metaPixelScript = document.createElement('script');
metaPixelScript.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '1956766861733295');
    fbq('track', 'PageView');
`;
document.head.appendChild(metaPixelScript);

// Add initialization check
const checkGAInitialized = setInterval(() => {
    if (window.gtag && window.GA_INITIALIZED) {
        clearInterval(checkGAInitialized);
    }
}, 1000);

setTimeout(() => {
    clearInterval(checkGAInitialized);
    if (!window.GA_INITIALIZED) {
        console.error('GA4 failed to initialize after 10 seconds');
    }
}, 10000);

// Use Vue plugins
Vue.use(VueRouter);
Vue.use(DashboardPlugin);
Vue.use(Toasted);

// Inject default icons, duration (6s) and fade-out animation for all toasts
const _toasted = Vue.prototype.$toasted;
const _defaultIcons = { success: 'check_circle', error: 'error', info: 'info', show: 'notifications' };
const _defaultDuration = 6000;
const _fadeOutDuration = 500;
Object.entries(_defaultIcons).forEach(([method, icon]) => {
    const original = _toasted[method].bind(_toasted);
    _toasted[method] = (msg, opts = {}) => {
        if (!opts.icon) opts.icon = icon;
        if (!opts.duration) opts.duration = _defaultDuration;
        const toast = original(msg, opts);
        // Schedule fade-out before removal
        if (toast && toast.el && opts.duration) {
            setTimeout(() => {
                if (toast.el) toast.el.classList.add('lixsa-toast-fade-out');
            }, opts.duration - _fadeOutDuration);
        }
        return toast;
    };
});
Vue.use(VueConfetti);
Vue.use(VueCarousel);
Vue.use(Particles, { init: async (engine) => await loadSlim(engine) });
Vue.use(VuePrism);
Vue.use(VueShepherd);

// Register global directive
Vue.directive('click-outside', clickOutside);

// All Material components use the `md-` prefix
Vue.config.ignoredElements = [/^md-/, /^md3-/];
// Ignoring MDUI components
Vue.config.ignoredElements = [/^mdui-/];

// Configure router
const router = new VueRouter({
    mode: 'history',
    routes, // short for routes: routes
    scrollBehavior: (to) => (to.hash ? { selector: to.hash } : { x: 0, y: 0 }),
    linkExactActiveClass: 'nav-item active',
});

// Las paginas se cargan por chunks al navegar (ver routes.js), asi que una pestana
// abierta durante un deploy pide un hash que ya no existe y la navegacion muere en
// silencio: el usuario pulsa y no pasa nada. Recargamos sobre la ruta destino, que
// pide el index nuevo y con el los hashes vigentes.
//
// El guardia de sessionStorage evita el bucle si el fallo no era por el deploy
// (offline, un chunk realmente roto): ahi reintentamos una vez y ya.
const CHUNK_RELOAD_FLAG = 'lixsa:chunk-reload';

// onError en vue-router 3 solo recibe el error, no la ruta destino, asi que la
// anotamos al entrar en la navegacion para poder recargar sobre ella y no sobre la
// pagina en la que el usuario sigue parado.
let pendingFullPath = null;
router.beforeEach((to, from, next) => {
    pendingFullPath = to.fullPath;
    next();
});

router.onError((error) => {
    const isChunkLoadError = /ChunkLoadError|Loading (CSS )?chunk .* failed/i.test(error?.message ?? '');
    if (!isChunkLoadError) return;

    if (sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
        console.error('Chunk load failed again after reloading; not retrying.', error);
        return;
    }

    sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
    window.location.assign(pendingFullPath ?? window.location.pathname);
});

router.afterEach(() => sessionStorage.removeItem(CHUNK_RELOAD_FLAG));

// Helper functions
function redirectToDashboardIfNeeded(to, isAuthenticated) {
    const isPublicPath = ['/', '/register', '/invite', '/forgot-password', '/pending-invitation'].includes(to.path);
    const shouldRedirect = isAuthenticated && isPublicPath && !to.query.logout;
    return shouldRedirect ? { name: 'Dashboard', query: to.query } : null;
}

function redirectToLoginIfNeeded(to, isAuthenticated) {
    if (to.matched.some((record) => record.meta.requiresAuth) && !isAuthenticated) {
        return { name: 'Login', query: to.query };
    }
    return null;
}

/**
 * Checks if the authenticated user has the required roles/permissions for the target route.
 * Prevents infinite redirect loops during onboarding or when already on the destination page.
 * @param {Object} to - The target route object.
 * @param {boolean} isAuthenticated - Whether the user is authenticated.
 * @param {string} userRole - The current user's role or permission level.
 * @returns {Object|null} A route object to redirect to, or null to proceed.
 */
function checkRolePermission(to, isAuthenticated, userRole) {
    if (isAuthenticated && to.matched.some((record) => record.meta.requiresAuth)) {
        // Skip permission checks for onboarding paths to avoid loops during initial setup
        const isOnboardingPath = to.path === '/onboard' || to.path === '/getting-started';
        if (isOnboardingPath) {
            return null;
        }

        const requiredRoles = to.meta.permissions;
        const lacksRequiredRole = requiredRoles && !requiredRoles.includes(userRole);
        if (lacksRequiredRole) {
            // Prevent infinite redirect loops if we are already going to or are on the Dashboard
            if (to.name === 'Dashboard' || to.path === '/dashboard') {
                return null;
            }
            return { name: 'Dashboard', query: to.query };
        }
    }
    return null;
}

function checkOnboardingRedirect(to, isAuthenticated) {
    if (store.getters['user/isDevMode']) return null;
    if (to.query.source?.toLowerCase() === 'shopify') return null;
    // Callback OAuth de TiendaNube: el code caduca en 5 minutos y es de un solo uso,
    // así que desviar a /onboard lo perdería y habría que reinstalar la app.
    if (to.query.code && to.params?.skillId === TIENDANUBE_SKILL_ID) return null;
    if (isAuthenticated && to.matched.some((record) => record.meta.requiresAuth)) {
        // Antes esto era `hasMetadata || isActiveAccount || !!account.uuid`, y ese
        // ultimo termino lo convertia en un no-op: toda cuenta creada tiene uuid, asi
        // que cualquiera que abandonase el alta a medias entraba igual al dashboard y
        // aterrizaba en /getting-started sin haber elegido nada.
        //
        // El criterio bueno es la marca que escribe markOnboardingComplete(). Se
        // conserva `isActiveAccount` como red de seguridad para las cuentas anteriores
        // a esa marca: si ya tienen un canal o una main app conectados, el alta esta
        // hecha de facto y no hay que devolverlas a /onboard.
        //
        // Es seguro comprobarlo aqui: el beforeEach ya ha esperado a user, account y
        // agentSettings antes de llegar a este punto.
        // La marca local va primero porque el `completed` del backend llega con
        // retraso: `GET /accounts/{id}` se sirve de una caché de 15 minutos que el
        // guardado del último paso no invalida a tiempo. Sin ella, quien acaba de
        // terminar el alta volvía a /onboard durante esa ventana.
        const hasFinishedOnboarding =
            isOnboardingCompletedLocally(store.state.user.user.account_id) ||
            store.getters['user/isOnboardingComplete'] ||
            store.getters['agentSettings/isActiveAccount'];

        // /getting-started NO es una excepcion: es la pantalla de despues del alta.
        // Solo /onboard queda exento, para no crear un bucle de redirecciones.
        const isOnboardingPath = to.path === '/onboard';

        if (!hasFinishedOnboarding && !isOnboardingPath) {
            return { path: '/onboard', query: to.query };
        }
    }
    return null;
}

function syncMembership() {
    try {
        store.dispatch('membership/getAccountMembership');
    } catch (error) {
        console.error('Error syncing membership: ', error);
    }
}

function syncAccount() {
    try {
        store.dispatch('user/actionGetAccount');
    } catch (error) {
        console.error('Error syncing account: ', error);
    }
}

function syncUser() {
    try {
        store.dispatch('user/actionGetUser');
    } catch (error) {
        console.error('Error syncing user: ', error);
    }
}

function syncStripeCustomer() {
    if (!store.getters['user/isShopifyBillingClient']) {
        try {
            store.dispatch('membership/fetchStripeSubscription', store.getters['membership/membershipId']);
        } catch (error) {
            console.error('Error syncing Stripe data:', error);
        }
    }
}

function syncAgentSettings() {
    try {
        store.dispatch('agentSettings/updateAgentSetting');
    } catch (error) {
        console.error('Error updating agent settings:', error);
    }
}

function syncTags() {
    try {
        store.dispatch('tags/fetchTags');
    } catch (error) {
        console.error('Error syncing tags:', error);
    }
}

function syncBlacklist() {
    try {
        store.dispatch('blacklist/fetchBlacklist');
    } catch (error) {
        console.error('Error syncing blacklist:', error);
    }
}

function initializeOnboardingTodos() {
    try {
        console.log('Initializing onboarding todos');
        store.dispatch('onboardingTodos/initializeTodos');
    } catch (error) {
        console.error('Error initializing onboarding todos:', error);
    }
}

// Main Navigation Guard
router.beforeEach(async (to, from, next) => {
    const isAuthenticated = store.getters['user/ifAuthenticated'];
    const isPendingInvitation = to.path === '/pending-invitation';

    // Sequentially await critical auth data if not yet loaded in Vuex.
    // Order matters: user → account (needs account_id) → agentSettings (needs auth).
    // This prevents stale-state redirects to /onboard on fresh page loads.
    if (isAuthenticated && !isPendingInvitation) {
        if (!store.state.user.user.uuid) {
            try {
                await store.dispatch('user/actionGetUser');
            } catch (error) {
                console.error('Error fetching user info on guard:', error);
            }
        }
        if (store.state.user.user.account_id && !store.state.user.account.uuid) {
            try {
                await store.dispatch('user/actionGetAccount');
            } catch (error) {
                console.error('Error fetching account info on guard:', error);
            }
        }
        // Fetch agentSettings if uuid is null (not yet loaded from API).
        // isActiveAccount getter depends on this; without it checkOnboardingRedirect may false-redirect.
        if (store.state.user.user.account_id && !store.state.agentSettings.agentSettings.uuid) {
            try {
                await store.dispatch('agentSettings/updateAgentSetting');
            } catch (error) {
                console.error('Error fetching agentSettings on guard:', error);
            }
        }
    }

    const userRole = store.state.user.user.permission;

    // Identify the user in Clarity with error handling
    if (store.getters['user/account'].uuid) {
        try {
            window.clarity('identify', store.getters['user/account'].uuid);
        } catch (error) {
            console.error('Error identifying user in Clarity:', error);
        }
    }

    // Redirect unauthenticated users to the login page for routes that require auth
    const loginRedirect = redirectToLoginIfNeeded(to, isAuthenticated && !isPendingInvitation);
    if (loginRedirect) return next(loginRedirect);

    // Redirect authenticated users from public paths to the dashboard
    const dashboardRedirect = redirectToDashboardIfNeeded(to, isAuthenticated && !isPendingInvitation);
    if (dashboardRedirect) {
        syncAccount();
        syncMembership();
        syncStripeCustomer();
        syncAgentSettings();
        syncTags();
        syncUser();
        syncBlacklist();
        return next(dashboardRedirect);
    }

    // Check if the authenticated user has the required role for the route
    const rolePermissionRedirect = checkRolePermission(to, isAuthenticated && !isPendingInvitation, userRole);
    if (rolePermissionRedirect) {
        syncAccount();
        syncMembership();
        syncStripeCustomer();
        syncAgentSettings();
        syncTags();
        syncUser();
        syncBlacklist();
        return next(rolePermissionRedirect);
    }

    // Check for onboarding redirect
    const onboardingRedirect = checkOnboardingRedirect(to, isAuthenticated && !isPendingInvitation);
    if (onboardingRedirect) {
        return next(onboardingRedirect);
    }

    // Proceed for all other cases
    const isPublicPath = ['/', '/register', '/invite', '/forgot-password', '/pending-invitation'].includes(to.path);
    if (!isPublicPath) {
        syncAccount();
        syncMembership();
        syncStripeCustomer();
        syncAgentSettings();
        syncTags();
        syncUser();
        syncBlacklist();
    }
    next();
});

// After each navigation, update document title
router.afterEach((to) => {
    Vue.nextTick(() => {
        document.title = to.meta.title ? `Lixsa | ${to.meta.title}` : 'Lixsa | Your Dream Team, Powered by AI';
    });
});

// Global library setup
Vue.prototype.$Chartist = Chartist;

// Register global components
Vue.component('TheModalWindow', TheModalWindow);
Vue.component('AgGridVue', AgGridVue);

// Patch ResizeObserver to prevent "ResizeObserver loop completed with undelivered notifications"
const OriginalResizeObserver = window.ResizeObserver;
window.ResizeObserver = class ResizeObserver extends OriginalResizeObserver {
    constructor(callback) {
        super((entries, observer) => {
            window.requestAnimationFrame(() => {
                if (!Array.isArray(entries) || !entries.length) {
                    return;
                }
                callback(entries, observer);
            });
        });
    }
};

// Initialize language before Vue instance creation
store.dispatch('user/initializeLanguage');

// Initialize Vue instance
new Vue({
    el: '#app',
    render: (h) => h(App),
    store,
    router,
    i18n,
    created() {
        // firebase-messaging-sw.js is NOT registered here on purpose. The Firebase SDK
        // registers it itself on getToken(), under its own scope
        // (/firebase-cloud-messaging-push-scope). Registering it here put it on the root
        // scope, where it fought with the PWA service worker registered by
        // ./registerServiceWorker — each page load swapped the worker owning "/", so the
        // other one reinstalled on the next load.

        this.$store.dispatch('websocket/connectSocket').then(() => {
            if (this.$store.getters['user/ifAuthenticated']) {
                this.$store.dispatch('membership/subscribeToMembershipUpdates');
                this.$store.dispatch('agentSettings/subscribeToAgentSettingsUpdates');
                this.$store.dispatch('tags/subscribeToTagUpdates');
                initializeOnboardingTodos();
            }
        });

        try {
            window.clarity('consent');
        } catch (error) {
            console.error('Error calling Clarity consent:', error);
        }
    },
});

// Prevent translation of icon elements
document.addEventListener('DOMContentLoaded', () => {
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    const icons = node.querySelectorAll
                        ? node.querySelectorAll('.material-symbols-outlined, md-icon')
                        : [];
                    icons.forEach((icon) => icon.setAttribute('translate', 'no'));

                    // Also check if the node itself is an icon
                    if (node.matches && node.matches('.material-symbols-outlined, md-icon')) {
                        node.setAttribute('translate', 'no');
                    }
                }
            });
        });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Handle existing icons
    document.querySelectorAll('.material-symbols-outlined, md-icon').forEach((icon) => {
        icon.setAttribute('translate', 'no');
    });
});

export { router };
